import {
  ROOT_NODE_ID,
  addAllReferencedComponents,
  getIdealGridWidth,
  getRootNode,
  resolveCapabilityMapViewSettings,
} from './utils';
import { Observable, combineLatest } from 'rxjs';
import type {
  ContextShape,
  EnhancedScopeData,
  GlobalReferenceType,
  GraphModelMap,
  GraphModelShape,
} from '@ardoq/data-model';
import {
  getComponentComparator,
  isTruthy,
  type ContextSort,
} from '@ardoq/common-helpers';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { componentOperations, toIds } from '@ardoq/core';
import {
  colorsAndRepresentations,
  componentUtils,
  filterColorDataOperations,
  scopeDataOperations,
  type FilterColorData,
} from '@ardoq/scope-data';
import { components$, ComponentsState } from 'streams/components/components$';
import { models$, ModelsState } from 'streams/models/models$';
import { filterCache } from 'collections/filterCache';
import { graphModel$ } from 'modelInterface/graphModel$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import {
  CapabilityMapModelData,
  LayoutBoxType,
  LayoutBoxTypes,
  ListValuesMap,
  NameLabelDict,
  ResolvedComponentData,
  ResolvedReferenceData,
  TemporaryDict,
  ViewSettingsShape,
  ViewStreamShape,
} from './types';
import { layoutRoot } from './layout';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { isEqual, sum } from 'lodash';
import { fieldInterface } from '@ardoq/field-interface';
import { dispatchAction, spreadMap } from '@ardoq/rxbeach';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import {
  getIconColorFromScopeData,
  getMaxTreeDepth,
} from '@ardoq/dependency-map';
import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  ViewIds,
} from '@ardoq/api-types';
import { getFilteredViewSettings$ } from 'views/filteredViewSettings$';
import { getComponentTypesInfo, type HasViewInstanceId } from '@ardoq/graph';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { modelUpdateNotification$ } from 'modelInterface/modelUpdateNotification$';
import { context$ } from 'streams/context/context$';
import { sort$ } from 'streams/context/sort$';
import { Locale, localeCompare } from '@ardoq/locale';
import type { I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { getFocusedStream, getHoveredStream } from 'tabview/streams';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';

const VIEW_ID = ViewIds.CAPABILITY_MAP;

export type StreamedProps = {
  viewModel: ViewStreamShape;
  viewState: ViewSettingsShape;
  i18nLocale: I18nLocale;
};

const emptyState: ViewStreamShape = {
  listFields: [],
  listValues: [],
  componentTypes: [],
  legendComponentTypes: [],
  supportingValue: '',
  focusedComponentId: null,
  hoveredComponentId: null,
  rootLayoutBox: {
    layoutType: LayoutBoxTypes.COLUMN,
    layoutBoxes: [],
    targetGridWidth: 0,
    hasPriorityValue: false,
    calculatedGridWidth: 0,
    id: '',
    classNames: '',
    representationData: undefined,
    leavesCount: 0,
  },
  referenceTypes: {
    targetReferenceTypes: [],
    sourceReferenceTypes: [],
  },
  maxTreeDepth: 0,
  workspaceId: '',
};

const fieldProps = ({ name, label }: APIFieldAttributes): NameLabelDict => ({
  name,
  label,
});

const valueOption = (name: string): NameLabelDict => ({
  name,
  label: name,
});

const prioritySorter = (a: TemporaryDict, b: TemporaryDict) => {
  const isAFinite = Number.isFinite(a.priority as number);
  const isBFinite = Number.isFinite(b.priority as number);

  return isAFinite && isBFinite
    ? (a.priority as number) - (b.priority as number)
    : isAFinite
      ? -1
      : isBFinite
        ? 1
        : a.navigatorOrder - b.navigatorOrder;
};

const getFieldsAndSelectedField = (
  workspaceId: string,
  fieldType: APIFieldType,
  selectedFieldName: string
) => {
  const listFields = fieldInterface.getFieldsOfWorkspace(workspaceId, [
    fieldType,
  ]);
  return {
    listFields: [{ name: '', label: 'None' }, ...listFields.map(fieldProps)],
    selectedField:
      selectedFieldName &&
      listFields.find(field => field.name === selectedFieldName),
  };
};

const getListValues = (selectedField: APIFieldAttributes) => {
  const listValues = fieldInterface.getAllowedValues(selectedField._id);
  const listValuesMap = listValues.reduce<ListValuesMap>(
    (result: ListValuesMap, value: string, index: number) => {
      result[value] = index;
      return result;
    },
    {}
  );
  return {
    listValues: [{ name: '', label: 'None' }, ...listValues.map(valueOption)],
    listValuesMap,
  };
};

const setMinGridWidth = (node: TemporaryDict) => {
  node.minGridWidth = 1;
  node.children.forEach(child => setMinGridWidth(child));
  if (node.priorityRows.length > 0) {
    node.minGridWidth =
      node.supportingColumns.length +
      node.priorityRows
        .map(list => sum(list.map(child => child.minGridWidth)))
        .reduce((max, cand) => (cand > max ? cand : max));
  } else if (
    node.id === ROOT_NODE_ID &&
    node.supportingColumns.length === 0 &&
    node.priorityRows.length === 0
  ) {
    node.minGridWidth = getIdealGridWidth(node.leavesCount);
  }
};

const setDescendantsLeafCount = (node: TemporaryDict) => {
  if (node.children.length === 0) {
    node.leavesCount = 1;
    return node.leavesCount;
  }
  node.leavesCount = sum(node.children.map(setDescendantsLeafCount));
  return node.leavesCount;
};

const getIds = (tempDict: TemporaryDict): string[] => [
  tempDict.id,
  ...tempDict.children.flatMap(getIds),
];
const getSubtree = (node: TemporaryDict): TemporaryDict[] => [
  ...node.children,
  ...node.children.flatMap(child => getSubtree(child)),
];
const collapseTree = (node: TemporaryDict, maxDepth: number): void => {
  if (maxDepth === 0) {
    node.collapsedDescendants = getSubtree(node);
  }
  node.children.forEach(child => collapseTree(child, maxDepth - 1));
  if (maxDepth <= 0) {
    node.supportingColumns.length = 0;
    node.priorityRows.length = 0;
    node.anyRow.length = 0;
    node.children.length = 0;
    node.isCollapsed = true;
  }
};

const getReferenceTypesForComponents = (
  componentIds: ArdoqId[],
  map: GraphModelMap
): GlobalReferenceType[] =>
  Object.values(
    Object.fromEntries(
      componentIds
        .flatMap(componentId =>
          (map.get(componentId) || []).map(({ referenceId }) => {
            const refType =
              referenceInterface.getGlobalReferenceType(referenceId);
            return refType ? [refType.id, refType] : null;
          })
        )
        .filter(isTruthy)
    )
  );

export const resolveComponentIconColor = (
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  component: APIComponentAttributes
): string => {
  const hasModelTypes = Boolean(scopeData.typesByModelId[component.model]);
  const hasFilterColor = Boolean(
    filterColorDataOperations.getComponentFilterColor(
      filterColorData,
      component._id
    )
  );
  if (!hasModelTypes && !hasFilterColor) {
    return '';
  }
  return getIconColorFromScopeData(
    scopeData,
    filterColorData,
    component._id,
    component._id,
    false
  );
};

export const getCapabilityMapModelData = (
  components: ComponentsState,
  graphModel: GraphModelShape,
  scopeData: EnhancedScopeData
): CapabilityMapModelData => {
  const includedComponents = components.models.filter(component =>
    componentInterface.isIncludedInContextByFilter(component)
  );
  const filterColorData = filterCache.getFilterColorData();
  const componentData = new Map<ArdoqId, ResolvedComponentData>(
    includedComponents.map((component): [ArdoqId, ResolvedComponentData] => [
      component._id,
      {
        attributes: component,
        childrenIds: toIds(
          componentUtils
            .getChildren(scopeData, component._id)
            .filter(child =>
              componentInterface.isIncludedInContextByFilter(child)
            )
        ),
        cssClassNames:
          componentUtils.getComponentCSSClassNames({
            scopeData,
            componentId: component._id,
            options: { useAsBackgroundStyle: true },
            filterColorData,
          }) ?? '',
        representationData:
          colorsAndRepresentations.getComponentRepresentationData(
            scopeData,
            component._id
          ),
        iconColor: resolveComponentIconColor(
          scopeData,
          filterColorData,
          component
        ),
      },
    ])
  );

  const referenceIds = [
    ...new Set(
      [...graphModel.targetMap.values(), ...graphModel.sourceMap.values()]
        .flat()
        .map(({ referenceId }) => referenceId)
    ),
  ];
  const referenceData = new Map<ArdoqId, ResolvedReferenceData>(
    referenceIds.map((referenceId): [ArdoqId, ResolvedReferenceData] => [
      referenceId,
      {
        globalTypeId: referenceInterface.getGlobalTypeId(referenceId),
        displayLabel: referenceInterface.getDisplayLabel(referenceId),
      },
    ])
  );

  return {
    componentData,
    referenceData,
    targetMap: graphModel.targetMap,
    sourceMap: graphModel.sourceMap,
  };
};

type CapabilityMapModelInput = {
  components: ComponentsState;
  modelData: CapabilityMapModelData;
};

type ScopeDataInputs = {
  components: ComponentsState;
  models: ModelsState;
  locale: Locale;
  sort: ContextSort;
};

type ScopeDataInput = {
  components: ComponentsState;
  scopeData: EnhancedScopeData;
};

export const toScopeDataInput = ({
  components,
  models,
  locale,
  sort,
}: ScopeDataInputs): ScopeDataInput => ({
  components,
  scopeData: scopeDataOperations.enhance(
    {
      ...scopeDataOperations.getEmpty(),
      components: [...components.models].sort(
        getComponentComparator(sort, locale)
      ),
      models: models.models,
    },
    { shouldFreeze: false }
  ),
});

const scopeDataInput$: Observable<ScopeDataInput> = combineLatest({
  components: components$,
  models: models$,
  locale: locale$,
  sort: sort$.pipe(distinctUntilChanged(isEqual)),
}).pipe(
  debounceTime(DEFAULT_DEBOUNCE_TIME),
  map(toScopeDataInput),
  shareReplay({ bufferSize: 1, refCount: true })
);

const getModelInput$ = (
  modelInterfaceUpdate$: Observable<null>
): Observable<CapabilityMapModelInput> =>
  combineLatest([scopeDataInput$, graphModel$, modelInterfaceUpdate$]).pipe(
    map(([{ components, scopeData }, graphModel]) => ({
      components,
      modelData: getCapabilityMapModelData(components, graphModel, scopeData),
    }))
  );

export const reset = (
  {
    selectedFieldName,
    supportingValue,
    groupingTypeId,
    isLayoutDirectionAnyVertical,
    includeIncomingReferenceTypes,
    includeOutgoingReferenceTypes,
    treeDepth,
  }: ViewSettingsShape,
  { workspaceId, componentId }: ContextShape,
  components: ComponentsState,
  hovered: ArdoqId | null,
  focused: ArdoqId | null,
  locale: Locale,
  modelData: CapabilityMapModelData
) => {
  const newState = {
    ...emptyState,
    workspaceId,
    hoveredComponentId: hovered,
    focusedComponentId: focused,
  };
  if (!workspaceId) {
    return newState;
  }

  const { listFields, selectedField } = getFieldsAndSelectedField(
    workspaceId,
    APIFieldType.LIST,
    selectedFieldName
  );
  newState.listFields = listFields;

  const { listValues, listValuesMap } = selectedField
    ? getListValues(selectedField)
    : {
        listValues: [],
        listValuesMap: {},
      };
  newState.listValues = listValues;

  const componentIds = componentId
    ? [componentId]
    : toIds(
        componentOperations
          .getRootComponents(workspaceId, components.models)
          .filter(component =>
            componentInterface.isIncludedInContextByFilter(component)
          )
      );

  const rootNode = getRootNode(
    modelData,
    selectedFieldName,
    groupingTypeId,
    prioritySorter,
    listValuesMap,
    supportingValue,
    componentIds
  );

  const compIds = getIds(rootNode);

  // componentTypes is used to populate the 'Select hidden component type' picker in the settings bar. for this we use the ids from the tree before it has been collapsed, and before references have been added.
  const { componentTypes } = getComponentTypesInfo(compIds);
  newState.componentTypes = componentTypes;

  const compareReferencesByName = (
    a: GlobalReferenceType,
    b: GlobalReferenceType
  ) => localeCompare(a.name, b.name, locale);

  newState.referenceTypes = {
    targetReferenceTypes: getReferenceTypesForComponents(
      compIds,
      modelData.targetMap
    ).toSorted(compareReferencesByName),
    sourceReferenceTypes: getReferenceTypesForComponents(
      compIds,
      modelData.sourceMap
    ).toSorted(compareReferencesByName),
  };
  // The root node is not a visible container, only expose the depth of
  // the visible tree.
  newState.maxTreeDepth = getMaxTreeDepth(rootNode) - 1;

  if (treeDepth < newState.maxTreeDepth) {
    collapseTree(rootNode, treeDepth);
  }

  rootNode.children.forEach(child =>
    addAllReferencedComponents(
      modelData,
      child,
      includeIncomingReferenceTypes,
      includeOutgoingReferenceTypes,
      selectedFieldName,
      groupingTypeId,
      prioritySorter,
      listValuesMap,
      supportingValue
    )
  );
  // legendComponentTypes should be everything that's actually visible. so we use the ids from the tree after it has been collapsed and after reference nodes have been
  newState.legendComponentTypes = getComponentTypesInfo(
    getIds(rootNode)
  ).legendComponentTypes;
  setDescendantsLeafCount(rootNode);
  setMinGridWidth(rootNode);

  newState.rootLayoutBox = layoutRoot(rootNode, isLayoutDirectionAnyVertical);

  return newState;
};

const getComponentIdsFromLayoutBox = (layoutBox: LayoutBoxType): ArdoqId[] => {
  return [
    layoutBox.id,
    ...layoutBox.layoutBoxes.flatMap(getComponentIdsFromLayoutBox),
  ].filter(isTruthy);
};

type CapabilityMapSettings =
  WorkspaceModeInstanceViewSettings<ViewIds.CAPABILITY_MAP>;

const toViewStreamShape = (
  viewstate: CapabilityMapSettings,
  context: ContextShape,
  hovered: ArdoqId | null,
  focused: ArdoqId | null,
  locale: Locale,
  { components, modelData }: CapabilityMapModelInput
): ViewStreamShape =>
  reset(
    viewstate.currentSettings,
    context,
    components,
    hovered,
    focused,
    locale,
    modelData
  );

const getViewModelStream = (
  modelInterfaceUpdate$: Observable<null>,
  viewSettings$: Observable<CapabilityMapSettings>
): Observable<ViewStreamShape> =>
  combineLatest([
    viewSettings$,
    context$,
    getHoveredStream(VIEW_ID),
    getFocusedStream(VIEW_ID),
    locale$,
    getModelInput$(modelInterfaceUpdate$),
  ]).pipe(
    spreadMap(toViewStreamShape),
    tap(({ rootLayoutBox }) => {
      dispatchAction(
        readViewComponentIds({
          [ViewIds.CAPABILITY_MAP]: getComponentIdsFromLayoutBox(rootLayoutBox),
        })
      );
    })
  );

/**
 * A treeDepth of null, or an invalid treeDepth, is coerced to Infinity (fully expanded). Because Infinity is not serializable as JSON, a saved slide is likely to have a null treeDepth.
 */
const resolveSettingsWithChanges = (
  settingsWithChanges: CapabilityMapSettings
) => ({
  ...settingsWithChanges,
  currentSettings: resolveCapabilityMapViewSettings(
    settingsWithChanges.currentSettings
  ),
});

export const getViewModel$ = ({
  viewInstanceId,
}: HasViewInstanceId): Observable<StreamedProps> => {
  const unfilteredResolvedViewSettings$ =
    getWorkspaceModeViewSettingsWithChanges$(VIEW_ID, viewInstanceId).pipe(
      map(resolveSettingsWithChanges)
    );

  // filters out isLegendActive by default
  const filteredViewSettings$ = getFilteredViewSettings$(
    unfilteredResolvedViewSettings$
  );

  return combineLatest([
    getViewModelStream(modelUpdateNotification$, filteredViewSettings$),
    unfilteredResolvedViewSettings$,
    i18nLocale$,
  ]).pipe(
    map(([viewModel, settingsWithChanges, i18nLocale]) => ({
      viewModel,
      viewState: settingsWithChanges.currentSettings,
      i18nLocale,
    }))
  );
};
